import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";

import useCurrentUser from "../../../hooks/useCurrentUser";
import { setResourceNotification } from "../../../redux/reducers/list";
import { Query } from "../../../types/firebase.types";
import { AppResource, ComparedDate } from "../../../types/resource.types";
import usePlaySound from "./usePlaySound";

const useCanceledNotification = (props: PropsType) => {
  const { query, resource, canReceiveNotifications = false, lastSeenAtField, canProceed } = props;
  const dispatch = useDispatch();
  const { user } = useCurrentUser();

  const hasNotification = useCallback(
    (comparisonDate: Date): boolean => {
      return dayjs(comparisonDate).isAfter((user as any)[lastSeenAtField] || user.creationTime);
    },
    [lastSeenAtField]
  );

  const playSound = usePlaySound();

  useEffect(() => {
    let unsubscribe: any;
    if (user.id && canReceiveNotifications)
      unsubscribe = query(user.id)
        .orderBy(ComparedDate.updatedAt, "desc")
        .limit(1)
        .onSnapshot({
          next(snapshots) {
            snapshots.docChanges().forEach((change) => {
              if (change.type === "added") {
                if (snapshots.size) {
                  const resultSnapshot = snapshots.docs[0];
                  const data = resultSnapshot.data();
                  if (canProceed?.(data)) {
                    const isCanceledNotification = hasNotification(data[ComparedDate.updatedAt].toDate());
                    dispatch(
                      setResourceNotification({
                        resource,
                        hasNotification: isCanceledNotification,
                      })
                    );
                    playSound(isCanceledNotification);
                  }
                }
              }
            });
          },
          error(ex) {
            console.log(ex.message);
          },
        });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user.id, query]);
};

interface PropsType {
  query: (id: string) => Query;
  resource: AppResource;
  canReceiveNotifications?: boolean;
  lastSeenAtField: string;
  canProceed?: (data: Record<string, any>) => boolean;
}

export default useCanceledNotification;
